import { formatCentsInMoneyTemplate } from '@/entrypoints/utils';
import { CheckoutEventChain } from '../internals/checkout-chain';
import { Cart, CartEvents, CartLine, TypedCartEvent } from '../internals/core';
import { CartEventBus } from '../internals/event-bus';
import { MiniCart } from './mini-cart';
import { createLogger } from '@/lib/logger';

export class MiniCartFooter extends HTMLElement {
  private abortController: AbortController | null = null;
  private eventBus: CartEventBus | null = null;
  private logger = createLogger('mini-cart-footer');
  private parentMiniCart: MiniCart | null = null;

  private checkoutButton: HTMLElement | null = null;
  private continueButton: HTMLElement | null = null;
  private totalPrice: HTMLElement | null = null;
  private comparePrice: HTMLElement | null = null;
  private priceFormat: string | null = null;
  private shipingProtectionInfo: HTMLElement | null = null;

  checkoutEventChain = new CheckoutEventChain();

  constructor() {
    super();
  }

  // Add internal logic here as separated functions
  private onCartChanged(e: TypedCartEvent<'cart:changed:debounced'>) {
    const { cart } = e.detail;
    this.render(cart);
  }

  private async addShippingProtectionProductIfNeeded(id: string) {
    if (this.classList.contains('simple') || sessionStorage.getItem('shipping-protection-removed'))
      return;

    const res = await fetch(`${window.Shopify.routes.root}cart/update.js?dormant=true`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        updates: {
          [parseInt(id)]: 1,
        },
      }),
    });
    if (!res.ok) throw new Error('Failed to add shipping protection product');
  }

  private async addOrRemoveShippingProtectionProductIfNeeded(cartItems: CartLine[], id: string) {
    const item = cartItems.find((item) => item.id === parseInt(id));
    const isSimple = this.classList.contains('simple');

    if (isSimple && item?.key) {
      return this.parentMiniCart!.cartApi.removeByKey(item.key);
    }

    if (!isSimple && !item) {
      return this.addShippingProtectionProductIfNeeded(id);
    }
  }

  private handleCheckoutButtonClick() {
    if (this.classList.contains('is-loading')) return;
    this.classList.add('is-loading');

    let stopRedirect = false;

    return new Promise((resolve, reject) => {
      const lineItems = this.parentMiniCart!.store.getLineItems();
      if (!lineItems) return reject(new Error('Failed to get line items'));

      const shippingProtectionProductId = this.getAttribute('data-shipping-protection-product') as
        | string
        | null;
      if (!shippingProtectionProductId)
        return reject(new Error('Failed to get shipping protection product id'));

      const currentCart = this.parentMiniCart?.store.getCart();
      if (!currentCart) return reject(new Error('Failed to get current cart'));

      if (currentCart.items.length == 1 && currentCart.items[0].handle == 'shipping-protection') {
        stopRedirect = true;
        return reject(new Error("Can't checkout with only Shipping protection in the cart"));
      }

      if (
        !currentCart.items.find(
          (item) =>
            item.handle != 'shipping-protection' &&
            !Object.keys(item.properties).includes('__free_gift'),
        ) &&
        currentCart.items.length > 0
      ) {
        stopRedirect = true;
        this.parentMiniCart?.cartApi.clear();
        return reject(new Error('Please add at least one product to the cart'));
      }

      return this.checkoutEventChain.execute(currentCart).then(() => {
        return this.addOrRemoveShippingProtectionProductIfNeeded(
          lineItems,
          shippingProtectionProductId,
        ).then(() => {
          resolve(true);
        });
      });
    })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        this.classList.remove('is-loading');
        if (!stopRedirect) {
          window.location.href = '/checkout';
        }
      });
  }

  private handleContinueButtonClick() {
    if (this.classList.contains('is-loading')) return;
    this.classList.add('is-loading');

    const self = this;

    const currentCart = this.parentMiniCart?.store.getCart();
    if (!currentCart) return;

    const lineItems = this.parentMiniCart!.store.getLineItems();
    if (!lineItems) return;

    const shippingProtectionProductId = this.getAttribute('data-shipping-protection-product') as
      | string
      | null;
    if (!shippingProtectionProductId) return;

    const shippingProtectionProduct = lineItems.find(
      (item) => item.id === parseInt(shippingProtectionProductId),
    );

    if (
      lineItems.length > 1 &&
      !lineItems.find(
        (item) =>
          item.handle != 'shipping-protection' &&
          !Object.keys(item.properties).includes('__free_gift'),
      )
    ) {
      self.classList.remove('is-loading');
      this.parentMiniCart?.cartApi.clear();
      return;
    }

    return this.checkoutEventChain.execute(currentCart).then(() => {
      if (shippingProtectionProduct) {
        this.parentMiniCart!.cartApi.removeByKey(shippingProtectionProduct.key!).then(() => {
          self.classList.remove('is-loading');
          window.location.href = '/checkout';
        });
      } else {
        self.classList.remove('is-loading');
        window.location.href = '/checkout';
      }
    });
  }

  private createShippingProtectionPopup() {
    const text = this.getAttribute('data-shipping-protection-popup-description') as string | null;
    if (!text || document.querySelector('.mini-cart__footer-shipping-protection-popup')) return;

    const popup = document.createElement('div');
    popup.innerHTML = `<p>${text}</p><div class="mini-cart__shipping-protection-popup-close"><svg width="27" height="27" viewBox="0 0 27 27" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M19.7818 7.31714L6.82861 20.2703" stroke="#333333" stroke-width="1.2" stroke-linecap="round"></path><path d="M20.0137 20.2637L7.06055 7.31052" stroke="#333333" stroke-width="1.2" stroke-linecap="round"></path></svg></div>`;
    popup.classList.add('mini-cart__footer-shipping-protection-popup');
    document.body.appendChild(popup);
  }

  private toggleShippingProtectionPopup(state?: any) {
    const popup = document.querySelector(
      '.mini-cart__footer-shipping-protection-popup',
    ) as HTMLElement | null;
    if (!popup) return;

    if (state !== undefined) {
      popup.classList.toggle('active', state);
      return;
    }

    popup.classList.toggle('active');
  }

  private render(cart: Cart) {
    const cartContainsMedispaService = !!cart.items.find((item) =>
      item.vendor.toLowerCase().includes('medispa'),
    );

    this.classList.toggle('simple', cartContainsMedispaService);

    const total = cart.total_price;
    const comparePrice = cart.original_total_price;
    const priceFormat = this.priceFormat;

    if (!priceFormat) return;

    if (this.totalPrice) {
      this.totalPrice.innerHTML = formatCentsInMoneyTemplate(priceFormat, total);
    }

    if (this.comparePrice) {
      this.comparePrice.innerHTML = formatCentsInMoneyTemplate(priceFormat, comparePrice);
    }

    this.comparePrice!.style.display =
      !this.comparePrice || total === comparePrice ? 'none' : 'block';

    this.style.visibility = cart.item_count > 0 ? 'visible' : 'hidden';
  }

  private bindEvents() {
    this.eventBus!.on(CartEvents.DebouncedChange, this.onCartChanged.bind(this));
    this.eventBus!.on(CartEvents.UIClosed, this.toggleShippingProtectionPopup.bind(this, false));

    this.shipingProtectionInfo?.addEventListener(
      'click',
      this.toggleShippingProtectionPopup.bind(this),
    );

    this.checkoutButton?.addEventListener('click', this.handleCheckoutButtonClick.bind(this));
    this.continueButton?.addEventListener('click', this.handleContinueButtonClick.bind(this));

    document
      .querySelector('.mini-cart__shipping-protection-popup-close')
      ?.addEventListener('click', this.toggleShippingProtectionPopup.bind(this, false));
  }

  private unbindEvents() {
    this.eventBus!.off(CartEvents.DebouncedChange, this.onCartChanged.bind(this));
    this.eventBus!.off(CartEvents.UIClosed, this.toggleShippingProtectionPopup.bind(this, false));

    this.shipingProtectionInfo?.removeEventListener(
      'click',
      this.toggleShippingProtectionPopup.bind(this),
    );

    this.checkoutButton?.removeEventListener('click', this.handleCheckoutButtonClick.bind(this));
    this.continueButton?.removeEventListener('click', this.handleContinueButtonClick.bind(this));

    document
      .querySelector('.mini-cart__shipping-protection-popup-close')
      ?.removeEventListener('click', this.toggleShippingProtectionPopup.bind(this, false));
  }

  private onMiniCartReady(parentMiniCart: MiniCart) {
    // Make sure we use the same event bus as the parent mini-cart.
    this.eventBus = parentMiniCart.bus;

    this.bindEvents();
  }

  // Lifecycle hooks
  connectedCallback() {
    this.abortController = new AbortController();

    this.checkoutButton = this.querySelector('.mini-cart__footer-checkout') as HTMLElement | null;
    this.continueButton = this.querySelector(
      '.mini-cart__footer-grid-button',
    ) as HTMLElement | null;
    this.totalPrice = this.querySelector('[data-total-price]') as HTMLElement | null;
    this.comparePrice = this.querySelector('[data-compare-price]') as HTMLElement | null;
    this.priceFormat = this.getAttribute('data-price-format') as string | null;
    this.shipingProtectionInfo = this.querySelector(
      '.mini-cart__footer-grid-checkout-plus-title',
    ) as HTMLElement | null;

    this.createShippingProtectionPopup();

    const parentMiniCart = this.closest('mini-cart')! as MiniCart;
    if (!parentMiniCart) {
      this.logger.error('MiniCartFooter must be a child of a MiniCart element');
      throw new Error('MiniCartFooter must be a child of a MiniCart element');
    }

    this.parentMiniCart = parentMiniCart;

    parentMiniCart.execOnReady(this.onMiniCartReady.bind(this, parentMiniCart));
  }

  disconnectedCallback() {
    this.abortController?.abort();

    this.unbindEvents();
  }
}

if (!customElements.get('mini-cart-footer')) {
  customElements.define('mini-cart-footer', MiniCartFooter);
}
